import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosClient from '../../api/axiosClient';
import { Plus, Edit2, Mail, Trash2 } from 'lucide-react';

const StaffPage = () => {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // State
  const [formData, setFormData] = useState({ full_name: '', email: '', password: '', role_id: '', is_active: true });

  // Data
  const { data: staff = [] } = useQuery({ queryKey: ['staff'], queryFn: () => axiosClient.get('/admin/staff') });
  const { data: roles = [] } = useQuery({ queryKey: ['roles'], queryFn: () => axiosClient.get('/admin/rbac/roles') });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data) => axiosClient.post('/admin/staff/create', { ...data, role: roles.find(r => r.id === parseInt(data.role_id))?.name }),
    onSuccess: () => { queryClient.invalidateQueries(['staff']); setShowForm(false); }
  });

  const updateMutation = useMutation({
    mutationFn: (data) => axiosClient.put(`/admin/staff/${editingId}`, data),
    onSuccess: () => { queryClient.invalidateQueries(['staff']); setShowForm(false); }
  });

  // ✅ NEW: Delete Mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => axiosClient.delete(`/admin/staff/${id}`),
    onSuccess: () => { 
        queryClient.invalidateQueries(['staff']);
        alert("Staff Member Deleted.");
    },
    onError: (err) => alert(err.response?.data?.detail || "Failed to delete staff.")
  });

  const handleEdit = (user) => {
      setEditingId(user.id);
      setFormData({ 
          full_name: user.full_name, email: user.email, 
          role_id: user.role_id, is_active: user.is_active, password: '' 
      });
      setShowForm(true);
  };

  const handleDelete = (id) => {
      if (window.confirm("Are you sure you want to remove this staff member? They will lose access immediately.")) {
          deleteMutation.mutate(id);
      }
  };

  return (
    <div className="space-y-6 animate-fade-in pb-10">
       <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-slate-800">Staff Management</h1>
        <button onClick={() => { setEditingId(null); setShowForm(!showForm); setFormData({ full_name: '', email: '', password: '', role_id: '', is_active: true }); }} className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-2" /> Add Staff
        </button>
      </div>

      {showForm && (
          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-lg mb-6">
              <h3 className="font-bold mb-4">{editingId ? 'Edit Staff Member' : 'New Staff Registration'}</h3>
              <form onSubmit={(e) => { e.preventDefault(); editingId ? updateMutation.mutate(formData) : createMutation.mutate(formData); }} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input placeholder="Full Name" className="p-2 border rounded" required value={formData.full_name} onChange={e => setFormData({...formData, full_name: e.target.value})} />
                  <input placeholder="Email" className="p-2 border rounded" required value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
                  
                  {!editingId && <input placeholder="Password" type="password" className="p-2 border rounded" required value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} />}
                  
                  <select className="p-2 border rounded bg-white" required value={formData.role_id} onChange={e => setFormData({...formData, role_id: e.target.value})}>
                      <option value="">-- Assign Role --</option>
                      {roles.filter(r => r.name !== 'customer').map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>

                  <label className="flex items-center space-x-2 border p-2 rounded cursor-pointer">
                      <input type="checkbox" checked={formData.is_active} onChange={e => setFormData({...formData, is_active: e.target.checked})} />
                      <span className="text-sm text-slate-600">Account Active</span>
                  </label>

                  <div className="md:col-span-2 pt-2 flex gap-2">
                      <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border rounded text-slate-500">Cancel</button>
                      <button type="submit" className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 flex-1">{editingId ? 'Update' : 'Create'}</button>
                  </div>
              </form>
          </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {staff.map(s => (
              <div key={s.id} className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-start justify-between group">
                  <div>
                      <h4 className="font-bold text-slate-800">{s.full_name}</h4>
                      <p className="text-xs text-slate-500 flex items-center mb-2"><Mail className="w-3 h-3 mr-1"/> {s.email}</p>
                      <div className="flex gap-2">
                        <span className="px-2 py-1 bg-purple-50 text-purple-700 text-[10px] font-bold uppercase rounded border border-purple-100">{s.role_name}</span>
                        {!s.is_active && <span className="px-2 py-1 bg-red-50 text-red-600 text-[10px] font-bold uppercase rounded">Inactive</span>}
                      </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => handleEdit(s)} className="p-2 text-slate-400 hover:text-blue-600 bg-slate-50 rounded-lg">
                          <Edit2 className="w-4 h-4"/>
                      </button>
                      {/* ✅ DELETE BUTTON (Hidden for Super Admin ID 1) */}
                      {s.id !== 1 && (
                          <button onClick={() => handleDelete(s.id)} className="p-2 text-slate-400 hover:text-red-600 bg-red-50 rounded-lg">
                              <Trash2 className="w-4 h-4"/>
                          </button>
                      )}
                  </div>
              </div>
          ))}
      </div>
    </div>
  );
};

export default StaffPage;